import React, {useState, useEffect, useRef} from 'react';
import {
	Box,
	Typography,
	useMediaQuery,
	Card,
	CardActionArea,
	Chip,
	IconButton, Backdrop, CircularProgress,
	Popover
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckIcon from '@mui/icons-material/Check';
import dayjs from 'dayjs';
import {useTheme} from '@mui/material/styles';
import {ThemeProvider} from "@mui/material/styles";
import {buttonTheme} from "../../theme";
import {config} from "../../config";
import {getVendorId, getSessionToken} from "../../utils/authSession";

const statusOptions = [
	{uiLabel: 'New', icon: '🆕', apiStatus: 'NEW'},
	{uiLabel: 'Processing', icon: '🔄', apiStatus: 'PROCESSING'},
	{uiLabel: 'Picked', icon: '✔️', apiStatus: 'PICKED'},
	{uiLabel: 'Packed', icon: '📦', apiStatus: 'PACKED'},
	{uiLabel: 'Shipped', icon: '🚚', apiStatus: 'SHIPPED'},
	{uiLabel: 'Out For Delivery', icon: '📍', apiStatus: 'OUT_FOR_DELIVERY'},
	{uiLabel: 'Delivered', icon: '✅', apiStatus: 'DELIVERED'},
	{uiLabel: 'Ready to Pickup', icon: '🛍️', apiStatus: 'READY_FOR_PICKUP'},
	{uiLabel: 'Picked Up', icon: '🙌', apiStatus: 'PICKED_UP'},
	{uiLabel: 'Cancelled', icon: '❌', apiStatus: 'CANCELLED'},
	{uiLabel: 'On Hold', icon: '⏸', apiStatus: 'ON_HOLD'},
];

export default function OrderFilters({filters, onFiltersChange}) {
	const theme = useTheme();
	const isMobile = useMediaQuery('(max-width:1050px)');

	const BASE = `${config.API_MWSSTOREFRONT_BASE_URL}/storefront/orm`;
	const vendorId = getVendorId();
	const sessionToken = getSessionToken();


	const dateContainerRef = useRef(null);

	const {selectedDate, selectedStatus} = filters;
	const [displayedWeek, setDisplayedWeek] = useState(
		selectedDate.clone().startOf('week')
	);

	const [statusAnchorEl, setStatusAnchorEl] = useState(null);
	const statusPopoverOpen = Boolean(statusAnchorEl);

	const [orderSummaries, setOrderSummaries] = useState([]);
	const [loadingSummaries, setLoadingSummaries] = useState(false);

	useEffect(() => {
		fetchOrderSummaries(displayedWeek);
	}, []);

	useEffect(() => {
		const newWeek = selectedDate.clone().startOf('week');
		setDisplayedWeek(newWeek);
		fetchOrderSummaries(newWeek);

		if (isMobile && dateContainerRef.current) {

			const dayIndex = selectedDate.diff(newWeek, 'day');
			const children = dateContainerRef.current.children;

			if (dayIndex >= 0 && dayIndex < children.length) {
				children[dayIndex].scrollIntoView({
					behavior: 'smooth',
					inline: 'center',
				});
			}
		}
	}, [selectedDate, isMobile]);

	async function fetchOrderSummaries(weekToFetch) {
		if (!vendorId) throw new Error('vendorId missing in localStorage');
		if (!sessionToken) throw new Error('sessionToken missing');

		// Sunday start; end inclusive (23:59) – keep your exact format
		const startDate = weekToFetch.startOf('week').format('YYYY-MM-DD[T]HH:mm');
		const endDate = weekToFetch.endOf('week').format('YYYY-MM-DD[T]HH:mm');

		const url =
			`${BASE}/${encodeURIComponent(vendorId)}/orders/summaries` +
			`?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`;

		setLoadingSummaries(true);
		try {
			const resp = await fetch(url, {headers: {'X-Session-Token': sessionToken}});
			if (!resp.ok) {
				const text = await resp.text().catch(() => '');
				throw new Error(`fetchOrderSummaries ${resp.status}: ${text}`);
			}
			const data = await resp.json();
			// store in component state so your cards can read it
			setOrderSummaries(Array.isArray(data) ? data : []);
		} catch (err) {
			console.error('Error fetching order summaries:', err);
			setOrderSummaries([]); // safe fallback
		} finally {
			setLoadingSummaries(false);
		}
	}

	const handleDateSelect = (date) => {
		if (!date.isAfter(dayjs(), 'day')) {
			onFiltersChange({selectedDate: date});
		}
	};

	const handleOpenStatus = (e) => {
		setStatusAnchorEl(e.currentTarget);
	};
	const handleCloseStatus = () => {
		setStatusAnchorEl(null);
	};

	const handleStatusChange = (apiStatus) => {
		const isSelected = selectedStatus.includes(apiStatus);
		const newStatus = isSelected
			? selectedStatus.filter((s) => s !== apiStatus)
			: [...selectedStatus, apiStatus];
		onFiltersChange({selectedStatus: newStatus});
	};

	const getStatusStyles = (status) => {
		switch (status) {
			case 'NEW':
				return {backgroundColor: '#2196F3', color: '#FFFFFF'};
			case 'PROCESSING':
				return {backgroundColor: '#FB8C00', color: '#FFFFFF'};
			case 'PICKED':
				return {backgroundColor: '#388E3C', color: '#FFFFFF'};
			case 'PACKED':
				return {backgroundColor: '#7B1FA2', color: '#FFFFFF'};
			case 'READY_FOR_PICKUP':
				return {backgroundColor: '#FBC02D', color: '#FFFFFF'};
			case 'PICKED_UP':
				return {backgroundColor: '#8BC34A', color: '#FFFFFF'};
			case 'SHIPPED':
				return {backgroundColor: '#673AB7', color: '#FFFFFF'};
			case 'OUT_FOR_DELIVERY':
				return {backgroundColor: '#009688', color: '#FFFFFF'};
			case 'DELIVERED':
				return {backgroundColor: '#4CAF50', color: '#FFFFFF'};
			case 'CANCELLED':
				return {backgroundColor: '#F44336', color: '#FFFFFF'};
			case 'ON_HOLD':
				return {backgroundColor: '#FFB300', color: '#FFFFFF'};
			case 'RETURNED':
				return {backgroundColor: '#FCE4EC', color: '#FFFFFF'};
			default:
				return {backgroundColor: '#9E9E9E', color: '#FFFFFF'};
		}
	};

	return (
		<Box sx={{margin: '0 auto', px: 1, pb: 1, display: 'flex', flexDirection: 'column'}}>
			<ThemeProvider theme={theme}>
				<Typography
					variant="subtitle1"
					sx={{
						fontWeight: 'bold',
						pt: isMobile ? '8px!important' : 0,
						py: isMobile ? 0 : 1,
						m: 0,
					}}
				>
					Select a Date
				</Typography>

				<Box
					ref={dateContainerRef}
					sx={{
						display: 'flex', flexWrap: isMobile ? 'nowrap' : 'wrap',
						overflowX: isMobile ? 'auto' : 'visible', gap: 1.5,
						width: '100%', borderWidth: 1, borderRadius: 2, p: 1,
						boxShadow: '1px 1px 5px 0px #bbb', borderColor: '#aaa'
					}}
				>
					{[...Array(7)].map((_, index) => {
						const date = displayedWeek.clone().startOf('week').add(index, 'day');
						const isSelected = date.isSame(filters.selectedDate, 'day');
						const isFuture = date.isAfter(dayjs(), 'day');

						const summary = orderSummaries.find(
							(s) => s.date === date.format('YYYY-MM-DD')
						);
						const orderCount = summary ? summary.orderCount : 0;
						const totalValue = summary ? summary.totalOrderValue : 0;

						let bgColor = '#f0f0f0';
						if (orderCount > 0) {
							bgColor = '#bfff00';
						}
						if (isSelected) {
							bgColor = theme.palette.secondary.main;
						}

						return (
							<Card
								key={index}
								sx={{
									flex: '0 0 auto',
									width: isMobile ? 140 : 140,
									height: isMobile ? 80 : 120,
									display: 'flex',
									flexDirection: 'column',
									justifyContent: 'space-between',
									backgroundColor: bgColor,
									cursor: isFuture ? 'not-allowed' : 'pointer',
									borderRadius: 2,
									boxShadow: isSelected ? 6 : isFuture ? 0 : 3,
									color: isSelected ? '#fff' : '#000',
									opacity: isFuture ? 0.6 : 1,
									'&:hover': {boxShadow: isFuture ? 0 : 6},
								}}
							>
								<CardActionArea
									onClick={() => {
										if (!isFuture) {
											handleDateSelect(date);
										}
									}}
									sx={{
										p: 1, cursor: isFuture ? 'not-allowed' : 'pointer',
										display: 'flex', flexDirection: 'column',
										justifyContent: 'space-between', alignItems: 'center',
										height: '100%', width: '100%',
									}}
								>
									<Box
										sx={{
											display: 'flex', alignItems: 'center', width: '100%',
											flexDirection: 'column', justifyContent: 'center'
										}}
									>
										<Typography
											variant={isMobile ? 'subtitle2' : 'h6'}
											fontWeight="bold"
										>
											{date.format('DD')}
										</Typography>
										<Typography
											variant="caption"
											color={isSelected ? '#fff' : theme.palette.text.secondary}
										>
											{date.format('ddd')}
										</Typography>
									</Box>

									<Box
										sx={{
											display: 'flex', justifyContent: 'space-between',
											width: '100%', fontSize: isMobile ? '0.7rem' : '0.8rem'
										}}
									>
										<Typography variant="caption">{orderCount} Orders</Typography>
										<Typography variant="caption">
											₹{Number(totalValue).toFixed(2)}
										</Typography>
									</Box>
								</CardActionArea>
							</Card>
						);
					})}
				</Box>

				<Box
					sx={{
						display: 'flex', alignItems: 'flex-end',
						justifyContent: 'space-between', py: isMobile ? 0 : 1
					}}
				>
					<Typography variant="subtitle1" sx={{fontWeight: 'bold'}}>
						Filter by Order Status
					</Typography>

					{isMobile && (
						<IconButton onClick={handleOpenStatus}>
							<MoreVertIcon/>
						</IconButton>
					)}
				</Box>

				{!isMobile && (
					<Box
						sx={{
							display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1,
							boxShadow: '1px 1px 5px 0px #bbb', borderRadius: 2, p: 1
						}}
					>
						{statusOptions.map((statusObj) => {
							const isSelected = selectedStatus.includes(statusObj.apiStatus);
							return (
								<Chip
									key={statusObj.apiStatus}
									label={statusObj.uiLabel}
									onClick={() => handleStatusChange(statusObj.apiStatus)}
									variant={isSelected ? 'filled' : 'outlined'}
									sx={{
										fontSize: '0.85rem', '&:hover': {color: '#000'},
										textShadow: '0 0 #000',
										backgroundColor: getStatusStyles(statusObj.apiStatus).backgroundColor,
										color: getStatusStyles(statusObj.apiStatus).color
									}}
									icon={
										isSelected ? (
											<CheckIcon
												sx={{
													fontSize: '1rem',
													color: '#000',
													backgroundColor: '#fff',
													borderRadius: 10,
												}}
											/>
										) : null
									}
								/>
							);
						})}
					</Box>
				)}
				<Popover
					open={statusPopoverOpen}
					anchorEl={statusAnchorEl}
					onClose={handleCloseStatus}
					anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
					transformOrigin={{vertical: 'top', horizontal: 'right'}}
					PaperProps={{
						sx: {
							p: 2, borderRadius: 2, display: 'flex',
							flexWrap: 'wrap', gap: 1, maxWidth: 300
						},
					}}
				>
					{statusOptions.map((statusObj) => {
						const isSelected = selectedStatus.includes(statusObj.apiStatus);
						return (
							<Chip
								key={statusObj.apiStatus}
								label={statusObj.uiLabel}
								onClick={() => handleStatusChange(statusObj.apiStatus)}
								variant={isSelected ? 'filled' : 'outlined'}
								sx={{
									fontSize: '0.75rem', textShadow: '0 0 #000',
									backgroundColor: getStatusStyles(statusObj.apiStatus).backgroundColor,
									color: getStatusStyles(statusObj.apiStatus).color, '&:hover': {color: '#000'},
								}}
								icon={
									isSelected ? (
										<CheckIcon
											sx={{
												fontSize: '1rem',
												color: '#000',
												backgroundColor: '#fff',
												borderRadius: 10,
											}}
										/>
									) : null
								}
							/>
						);
					})}
				</Popover>
				<Backdrop
					open={loadingSummaries}
					sx={{color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1}}
				>
					<CircularProgress color="inherit"/>
				</Backdrop>
			</ThemeProvider>
		</Box>
	);
}
